"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useRef, useEffect } from "react";
import { Link } from "@/components/LocaleLink";
import { Briefcase, ChevronDown, FileText, Linkedin, Mail, MessageSquare, Mic, Search } from "lucide-react";
interface MegaMenuProps {
  locale: string;
  isAuthenticated: boolean;
}
const solutions = {
  fr: {
    preparation: {
      title: "Préparation",
      items: [{
        icon: MessageSquare,
        title: "Entretiens IA",
        desc: "Questions personnalisées, feedback détaillé",
        href: "/interviews/new",
        authHref: "/interviews/new"
      }, {
        icon: Mic,
        title: "Entretien Live",
        desc: "Simulation vocale avec IA en temps réel",
        href: "/interviews/new?type=live",
        authHref: "/interviews/new?type=live",
        badge: "Premium"
      }]
    },
    cv: {
      title: "Outils CV",
      items: [{
        icon: Search,
        title: "Analyse ATS",
        desc: "Score de compatibilité et mots-clés",
        href: "/analyse-cv",
        authHref: "/ats"
      }, {
        icon: FileText,
        title: "CV Builder",
        desc: "Templates pros optimisés ATS",
        href: "/resumes",
        authHref: "/resumes"
      }, {
        icon: Mail,
        title: "Lettre de motivation IA",
        desc: "Générée en 30 secondes",
        href: "/dashboard",
        authHref: "/cover-letters",
        badge: "Nouveau"
      }]
    },
    optimisation: {
      title: "Optimisation",
      items: [{
        icon: Linkedin,
        title: "LinkedIn IA",
        desc: "Analyse et optimise ton profil",
        href: "/dashboard",
        authHref: "/linkedin",
        badge: "Nouveau"
      }, {
        icon: Briefcase,
        title: "Suivi des candidatures",
        desc: "Kanban, rappels, analytics",
        href: "/dashboard",
        authHref: "/applications"
      }]
    }
  },
  en: {
    preparation: {
      title: "Preparation",
      items: [{
        icon: MessageSquare,
        title: "AI Interviews",
        desc: "Personalized questions, detailed feedback",
        href: "/interviews/new",
        authHref: "/interviews/new"
      }, {
        icon: Mic,
        title: "Live Interview",
        desc: "Real-time voice simulation with AI",
        href: "/interviews/new?type=live",
        authHref: "/interviews/new?type=live",
        badge: "Premium"
      }]
    },
    cv: {
      title: "Resume Tools",
      items: [{
        icon: Search,
        title: "ATS Analysis",
        desc: "Compatibility score and keywords",
        href: "/analyse-cv",
        authHref: "/ats"
      }, {
        icon: FileText,
        title: "Resume Builder",
        desc: "ATS-optimized pro templates",
        href: "/resumes",
        authHref: "/resumes"
      }, {
        icon: Mail,
        title: "AI Cover Letter",
        desc: "Generated in 30 seconds",
        href: "/dashboard",
        authHref: "/cover-letters",
        badge: "New"
      }]
    },
    optimisation: {
      title: "Optimization",
      items: [{
        icon: Linkedin,
        title: "LinkedIn AI",
        desc: "Analyze and optimize your profile",
        href: "/dashboard",
        authHref: "/linkedin",
        badge: "New"
      }, {
        icon: Briefcase,
        title: "Application tracker",
        desc: "Kanban, reminders, analytics",
        href: "/dashboard",
        authHref: "/applications"
      }]
    }
  }
};
export function MegaMenu(t0) {
  const $ = _c(18);
  const {
    locale,
    isAuthenticated
  } = t0;
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef(undefined);
  const menuRef = useRef(null);
  const data = locale === "fr" ? solutions.fr : solutions.en;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      clearTimeout(timeoutRef.current);
      setOpen(true);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleEnter = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      timeoutRef.current = setTimeout(() => setOpen(false), 200);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleLeave = t2;
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => () => clearTimeout(timeoutRef.current);
    t4 = [];
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  useEffect(t3, t4);
  let t5;
  if ($[4] !== open) {
    t5 = () => setOpen(!open);
    $[4] = open;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const t6 = `h-3.5 w-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`;
  let t7;
  if ($[6] !== t6) {
    t7 = <ChevronDown className={t6} />;
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== t5 || $[9] !== t7) {
    t8 = <button onClick={t5} className="link-slide flex items-center gap-1 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">Solutions{t7}</button>;
    $[8] = t5;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== data || $[12] !== isAuthenticated || $[13] !== open) {
    t9 = open && <div className="absolute left-1/2 top-full z-50 mt-2 w-[520px] max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded-xl border border-border/50 bg-background/95 p-4 shadow-xl backdrop-blur-xl"><div className="grid grid-cols-2 gap-4">{Object.values(data).map(group => <div key={group.title}><p className="mb-2 px-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground/60">{group.title}</p><div className="space-y-1">{group.items.map(item => <Link key={item.title} href={isAuthenticated ? item.authHref : item.href} onClick={() => setOpen(false)} className="flex items-start gap-3 rounded-lg px-2 py-2 transition-colors hover:bg-muted/50"><item.icon className="mt-0.5 h-4 w-4 shrink-0 text-primary" /><div className="min-w-0"><div className="flex items-center gap-2"><span className="text-sm font-medium">{item.title}</span>{"badge" in item && item.badge && <span className="rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-medium text-primary">{item.badge}</span>}</div><p className="text-xs text-muted-foreground">{item.desc}</p></div></Link>)}</div></div>)}</div></div>;
    $[11] = data;
    $[12] = isAuthenticated;
    $[13] = open;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t8 || $[16] !== t9) {
    t10 = <div ref={menuRef} className="relative" onMouseEnter={handleEnter} onMouseLeave={handleLeave}>{t8}{t9}</div>;
    $[15] = t8;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  return t10;
}